import * as React from 'react';

import classnames from 'classnames';

import type {CoreEntityType, User} from 'nutshell-core/types';
import {getMultipleEntityType} from 'nutshell-core/utils/get-multiple-entity-type';

import type {ListOptionWithCount} from 'shells/select-option-components/option-with-count';

import type {Flow} from 'flow-to-typescript-codemod';

import {
    shouldShowMailchimp,
    shouldShowConstantContact,
} from '../../../integrations/should-show-buttons';

import {AddToAudienceButton} from './add-to-audience-button';
import {ConstantContactButton} from './constant-contact-button';
import type {Props as ConstantContactButtonOwnProps} from './constant-contact-button';
import {DeleteButton} from './delete-button';
import type {Props as DeleteButtonProps} from './delete-button';
import {EditButton} from './edit-button';
import {MergeButton} from './merge-button';
import type {Props as MergeButtonProps} from './merge-button';

import styles from './actions.module.css';

type ConstantContactButtonProps = Flow.Diff<
    ConstantContactButtonOwnProps,
    {
        isDisabled: boolean;
    }
>;

export type Props = {
    filters: any[];
    selectedCount: number;
    omittedCount: number;
    maxMergeCount: number;
    massSelect: boolean;
    entityType: CoreEntityType;
    handleEditClick: () => void;
    handleAddToAudienceSubmit: (
        audience: ListOptionWithCount,
        onlyPrimaryContact: boolean
    ) => Promise<any>;
    user: User;
} & ConstantContactButtonProps &
    DeleteButtonProps &
    MergeButtonProps;

export function ActionsDefault(props: Props) {
    const noneSelected = props.selectedCount < 1;

    const canAccessCrm = props.user.permissions.canAccessCrm;

    const isDuplicatesMode = props.filters.find((entry) => entry.duplicates === true) !== undefined;

    const showEditButton = props.user.permissions.canBulkEdit && !isDuplicatesMode;

    const showMergeButton = props.maxMergeCount !== 0 && props.user.permissions.canMergeEntities;

    const shouldShowMailchimpButton =
        shouldShowMailchimp(NutClientConfig.integrations) && !isDuplicatesMode && canAccessCrm;

    const areEmailMarketingExportsDisabled = !props.massSelect && props.selectedCount === 0;

    const shouldShowConstantContactButton =
        shouldShowConstantContact(NutClientConfig.integrations) &&
        !isDuplicatesMode &&
        canAccessCrm;

    return (
        <>
            {showEditButton && (
                <EditButton isDisabled={noneSelected} onClick={props.handleEditClick}>
                    Edit
                </EditButton>
            )}
            {showMergeButton && (
                <MergeButton
                    onMerge={props.onMerge}
                    entityType={props.entityType}
                    massSelect={props.massSelect}
                    displayName={props.displayName}
                    allItems={props.allItems}
                    selectedItems={props.selectedItems}
                    maxMergeCount={props.maxMergeCount}
                    omittedCount={props.omittedCount}
                    selectedCount={props.selectedCount}
                    onMergeLeads={props.onMergeLeads}
                />
            )}
            {shouldShowMailchimpButton && (
                <button
                    type='button'
                    disabled={areEmailMarketingExportsDisabled}
                    data-url={`/${getMultipleEntityType(
                        props.entityType
                    )}/mailchimp-popover.json?orientation=north`}
                    className={classnames(
                        'popover-opener-mailchimp popover-opener-ajax popover-opener-north',
                        styles['mailchimp-button']
                    )}
                >
                    Send to MailChimp
                </button>
            )}
            {shouldShowConstantContactButton && (
                <ConstantContactButton
                    isDisabled={areEmailMarketingExportsDisabled}
                    onExportToConstantContact={props.onExportToConstantContact}
                    selectedCount={props.selectedCount}
                    displayName={props.displayName}
                    displayNameSingular={props.displayNameSingular}
                    user={props.user}
                    handleConstantContactSubmit={props.handleConstantContactSubmit}
                />
            )}
            {!isDuplicatesMode && props.user.permissions.canAccessMarketing && (
                <AddToAudienceButton
                    entityType={props.entityType}
                    selectedCount={props.selectedCount}
                    isDisabled={areEmailMarketingExportsDisabled}
                    onSubmit={props.handleAddToAudienceSubmit}
                />
            )}
            {props.user.permissions.canDeleteEntities && (
                <DeleteButton
                    selectedCount={props.selectedCount}
                    displayName={props.displayName}
                    handleDelete={props.handleDelete}
                />
            )}
        </>
    );
}
